import { useState } from 'react';
import { Banner, BlockStack, Button, Modal, Text, TextField } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

type Preview = {
  all_conditions_match: boolean;
  condition_results: Array<{
    condition: { field: string; operator: string; value: unknown };
    field_value?: unknown;
    matched: boolean;
  }>;
  actions_would_execute: unknown[];
};

export default function AutomationPreview({ rule, close }: {
  rule: { id: number; name: string; conditions: Array<{ field: string; value: unknown }> };
  close(): void;
}) {
  const { fetchApi, loading, error } = useApi();
  const [sample, setSample] = useState(JSON.stringify(
    Object.fromEntries(rule.conditions.map(c => [c.field, c.value])), null, 2,
  ));
  const [validation, setValidation] = useState('');
  const [result, setResult] = useState<Preview | null>(null);

  const preview = async () => {
    setResult(null);
    setValidation('');
    let data: unknown;
    try {
      data = JSON.parse(sample);
    } catch {
      setValidation('Enter a JSON object, for example {"order_total": 100}.');
      return;
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      setValidation('Sample data must be an object with field names and values.');
      return;
    }
    const reply = await fetchApi<{ data: Preview }>(`/automation/rules/${rule.id}/test`, {
      method: 'POST', body: JSON.stringify({ test_data: data }),
    });
    if (reply) setResult(reply.data);
  };

  return (
    <Modal
      open
      title={`Preview: ${rule.name}`}
      onClose={close}
      secondaryActions={[{ content: 'Close', onAction: close }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          <Text as="p">
            Enter sample values to check this saved rule. Starting values copy the rule's conditions;
            replace them with the values you want to test. This preview changes no orders and sends no messages.
          </Text>
          <TextField
            label="Sample event data (JSON)"
            value={sample}
            onChange={value => { setSample(value); setResult(null); }}
            multiline={8}
            autoComplete="off"
            maxLength={30000}
            disabled={loading}
          />
          {(validation || error) && <Banner tone="critical">{validation || error}</Banner>}
          <Button onClick={() => void preview()} loading={loading}>Preview conditions</Button>
          {result && <>
            <Banner tone={result.all_conditions_match ? 'success' : 'info'}>
              {result.all_conditions_match
                ? 'All conditions match this sample.'
                : 'This sample does not match every condition.'} No actions were run.
            </Banner>
            {result.condition_results.map((row, index) => (
              <Text as="p" key={index}>
                {row.matched ? 'Match' : 'No match'}: {row.condition.field} {row.condition.operator} {JSON.stringify(row.condition.value)}.
                Sample: {JSON.stringify(row.field_value) ?? 'missing'}.
              </Text>
            ))}
            <Text as="h3" variant="headingSm">{result.actions_would_execute.length} matching action(s)</Text>
            {result.actions_would_execute.map((action, index) => (
              <Text as="p" key={index}>{JSON.stringify(action)}</Text>
            ))}
          </>}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
